import { ReactNode, useCallback, useContext, useEffect, useRef } from "react";

import { Box, Stack, ThemeProvider } from "@mui/material";

import { zeffyTheme } from "../../../../common/theme/theme";
import { NotificationBannerHeightContext } from "../NotificationBannerHeightContext";
import { useSyncNotificationBannerHeight } from "../useSyncNotificationBannerHeight";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { ChevronUp, Reveal as Eye } from "@/icons/outlined";

const EXPAND_BUTTON_WIDTH = 48;

export type OwnerBannerLayoutProps = {
  slots?: {
    info?: ReactNode;
    callout?: ReactNode;
    actions?: ReactNode;
  };
};

export const OwnerBannerLayout = ({ slots }: OwnerBannerLayoutProps) => {
  const { isSmallScreen } = useMediaQuery();
  const { t } = useTranslate();

  const { isExpanded, setIsExpanded } = useContext(NotificationBannerHeightContext);
  const containerRef = useSyncNotificationBannerHeight();
  const height = containerRef.current?.offsetHeight || 0;
  const expandButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setIsExpanded(true);
    return () => setIsExpanded(false);
  }, [setIsExpanded]);

  const onExpandClick = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded, setIsExpanded]);

  return (
    <ThemeProvider theme={zeffyTheme}>
      <Box
        sx={{
          position: "sticky",
          top: isExpanded ? 0 : `-${height}px`,
          zIndex: 1000,
          display: isSmallScreen ? "flex" : "grid",
          flexDirection: isSmallScreen ? "column" : undefined,
          gridTemplateColumns: isSmallScreen ? undefined : "1fr auto 1fr",
          alignItems: "center",
          backgroundColor: zeffyTheme.palette.surface.neutral.white,
          borderBottomWidth: 1,
          borderBottomStyle: "solid",
          borderColor: zeffyTheme.palette.border.form.quiet,
          paddingTop: zeffyTheme.spacing(isSmallScreen ? 1.5 : 2),
          paddingBottom: zeffyTheme.spacing(isSmallScreen ? 1.5 : 2),
          paddingX: zeffyTheme.spacing(isSmallScreen ? 2 : 6),
          marginTop: isExpanded ? 0 : `-${height}px`,
          transition: "margin 0.3s ease-out, top 0.3s ease-out",
        }}
        ref={containerRef}
        data-test="form-owner-banner"
      >
        {/* Info section - aligned left */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ justifySelf: "start" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <DashboardIcon>
              <Eye />
            </DashboardIcon>
          </Box>
          <Box>
            <DashboardTypography variant="subtitle1" vibe="text-neutral-moderate">
              {t("common", "adminView")}
            </DashboardTypography>
          </Box>
          {slots?.info}
        </Stack>

        {/* Callout section - centered */}
        <Box sx={{ justifySelf: "center" }}>{slots?.callout}</Box>

        {/* Actions section - aligned right */}
        <Box sx={{ justifySelf: isSmallScreen ? undefined : "end" }}>{slots?.actions}</Box>

        {/* Expand button */}
        <Box
          ref={expandButtonRef}
          sx={{
            position: "absolute",
            left: `calc(50% - ${EXPAND_BUTTON_WIDTH / 2}px)`,
            bottom: `-${expandButtonRef?.current?.offsetHeight || 0}px`,
          }}
          data-test="form-owner-banner-expand-button"
        >
          <DashboardIconButton
            variant="text"
            vibe="neutral"
            size="small"
            sx={{
              zIndex: 200,
              width: `${EXPAND_BUTTON_WIDTH}px`,
              backgroundColor: zeffyTheme.palette.surface.neutral.white,
              border: `1px solid ${zeffyTheme.palette.border.form.quiet}`,
              borderRadius: "0 0 8px 8px !important",
            }}
            slotProps={{
              icon: {
                sx: {
                  transition: "transform 0.3s ease-out",
                  transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
                },
              },
            }}
            onClick={onExpandClick}
          >
            <ChevronUp />
          </DashboardIconButton>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
